/**
 * Users Management Modal Component
 * Admin-only interface for managing system users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useViewOnly } from '../../contexts';
import './Modal.css';

const UsersModal = ({ isOpen, onClose, language }) => {
  const { authFetch, user: currentUser } = useAuth();
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit/Create state
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'receptionist'
  });

  // Translations
  const t = {
    title: language === 'he' ? 'ניהול משתמשים' : 'Manage Users',
    addUser: language === 'he' ? 'הוסף משתמש' : 'Add User',
    email: language === 'he' ? 'אימייל' : 'Email',
    password: language === 'he' ? 'סיסמה' : 'Password',
    firstName: language === 'he' ? 'שם פרטי' : 'First Name',
    lastName: language === 'he' ? 'שם משפחה' : 'Last Name',
    role: language === 'he' ? 'תפקיד' : 'Role',
    status: language === 'he' ? 'סטטוס' : 'Status',
    lastLogin: language === 'he' ? 'התחברות אחרונה' : 'Last Login',
    actions: language === 'he' ? 'פעולות' : 'Actions',
    active: language === 'he' ? 'פעיל' : 'Active',
    inactive: language === 'he' ? 'מושבת' : 'Inactive',
    edit: language === 'he' ? 'עריכה' : 'Edit',
    deactivate: language === 'he' ? 'השבת' : 'Deactivate',
    activate: language === 'he' ? 'הפעל' : 'Activate',
    resetPassword: language === 'he' ? 'אפס סיסמה' : 'Reset Password',
    save: language === 'he' ? 'שמור' : 'Save',
    cancel: language === 'he' ? 'ביטול' : 'Cancel',
    create: language === 'he' ? 'צור משתמש' : 'Create User',
    saving: language === 'he' ? 'שומר...' : 'Saving...',
    noUsers: language === 'he' ? 'אין משתמשים' : 'No users found',
    loadError: language === 'he' ? 'שגיאה בטעינת משתמשים' : 'Error loading users',
    never: language === 'he' ? 'מעולם לא' : 'Never',
    you: language === 'he' ? '(את/ה)' : '(you)',
    includeInactive: language === 'he' ? 'הצג משתמשים מושבתים' : 'Show inactive users',
    confirmDeactivate: language === 'he' ? 'האם להשבית את המשתמש?' : 'Deactivate this user?'
  };

  // Role translations
  const roleLabels = {
    admin: language === 'he' ? 'מנהל' : 'Admin',
    doctor: language === 'he' ? 'רופא' : 'Doctor',
    receptionist: language === 'he' ? 'קבלה' : 'Receptionist',
    assistant: language === 'he' ? 'עוזר' : 'Assistant'
  };

  const [includeInactive, setIncludeInactive] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`/api/auth/users?include_inactive=${includeInactive}`);
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users || []);
      } else {
        setError(data.error || t.loadError);
      }
    } catch (err) {
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  }, [authFetch, includeInactive, t.loadError]);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await authFetch('/api/auth/roles');
      const data = await response.json();
      if (response.ok) {
        setRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchRoles();
    }
  }, [isOpen, fetchUsers, fetchRoles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'receptionist'
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(language === 'he' ? 'המשתמש נוצר בהצלחה' : 'User created successfully');
        resetForm();
        fetchUsers();
      } else {
        setError(data.error || data.detail?.error || 'Error creating user');
      }
    } catch (err) {
      setError('Error creating user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch(`/api/auth/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role
        })
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(language === 'he' ? 'המשתמש עודכן בהצלחה' : 'User updated successfully');
        resetForm();
        fetchUsers();
      } else {
        setError(data.error || data.detail?.error || 'Error updating user');
      }
    } catch (err) {
      setError('Error updating user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm(t.confirmDeactivate)) return;

    try {
      const response = await authFetch(`/api/auth/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess(language === 'he' ? 'המשתמש הושבת' : 'User deactivated');
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error || 'Error deactivating user');
      }
    } catch (err) {
      setError('Error deactivating user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const response = await authFetch(`/api/auth/users/${userId}/activate`, {
        method: 'POST'
      });

      if (response.ok) {
        setSuccess(language === 'he' ? 'המשתמש הופעל' : 'User activated');
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error || 'Error activating user');
      }
    } catch (err) {
      setError('Error activating user');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt(language === 'he' ? 'הזן סיסמה חדשה (לפחות 8 תווים):' : 'Enter new password (at least 8 characters):');
    if (!newPassword || newPassword.length < 8) {
      if (newPassword) {
        setError(language === 'he' ? 'הסיסמה חייבת להכיל לפחות 8 תווים' : 'Password must be at least 8 characters');
      }
      return;
    }

    try {
      const response = await authFetch(`/api/auth/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        setSuccess(language === 'he' ? 'הסיסמה אופסה בהצלחה' : 'Password reset successfully');
      } else {
        const data = await response.json();
        setError(data.error || 'Error resetting password');
      }
    } catch (err) {
      setError('Error resetting password');
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role
    });
    setShowCreateForm(false);
    setError('');
    setSuccess('');
  };

  const startCreate = () => {
    resetForm();
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  if (!isOpen) return null;

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{t.title}</h2>
            <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
          </div>
          <div className="modal-body">
            <p className="text-center">
              {language === 'he' ? 'אין לך הרשאה לצפות בעמוד זה' : 'You do not have permission to view this page'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t.title}</h2>
          <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Toolbar */}
          <div className="toolbar">
            <button
              className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
              onClick={startCreate}
              disabled={isViewOnly}
              title={isViewOnly ? viewOnlyTip : undefined}
            >
              + {t.addUser}
            </button>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t.includeInactive}
            </label>
          </div>

          {/* Create/Edit Form */}
          {(showCreateForm || editingUser) && (
            <form
              onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
              className="user-form"
            >
              <h3>{editingUser ? t.edit : t.addUser}</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>{t.firstName}</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t.lastName}</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {!editingUser && (
                <div className="form-row">
                  <div className="form-group">
                    <label>{t.email}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t.password}</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>{t.role}</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {language === 'he' ? role.label_he : role.label_en}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
                  disabled={loading || isViewOnly}
                  title={isViewOnly ? viewOnlyTip : undefined}
                >
                  {loading ? t.saving : (editingUser ? t.save : t.create)}
                </button>
              </div>
            </form>
          )}

          {/* Users Table */}
          <div className="users-table-container">
            {loading && !users.length ? (
              <div className="loading-spinner">Loading...</div>
            ) : users.length === 0 ? (
              <p className="text-center">{t.noUsers}</p>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{t.email}</th>
                    <th>{language === 'he' ? 'שם' : 'Name'}</th>
                    <th>{t.role}</th>
                    <th>{t.status}</th>
                    <th>{t.lastLogin}</th>
                    <th>{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                      <td>
                        {user.email}
                        {user.id === currentUser?.id && <span className="you-badge">{t.you}</span>}
                      </td>
                      <td>{user.full_name || `${user.first_name} ${user.last_name}`}</td>
                      <td>{roleLabels[user.role] || user.role}</td>
                      <td>
                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? t.active : t.inactive}
                        </span>
                      </td>
                      <td>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')
                          : t.never}
                      </td>
                      <td className="actions-cell">
                        <button
                          className={`btn-icon ${isViewOnly ? 'disabled-view-only' : ''}`}
                          onClick={() => startEdit(user)}
                          disabled={isViewOnly}
                          title={isViewOnly ? viewOnlyTip : t.edit}
                        >
                          ✏️
                        </button>
                        {user.id !== currentUser?.id && (
                          <>
                            {user.is_active ? (
                              <button
                                className={`btn-icon ${isViewOnly ? 'disabled-view-only' : ''}`}
                                onClick={() => handleDeactivateUser(user.id)}
                                disabled={isViewOnly}
                                title={isViewOnly ? viewOnlyTip : t.deactivate}
                              >
                                🚫
                              </button>
                            ) : (
                              <button
                                className={`btn-icon ${isViewOnly ? 'disabled-view-only' : ''}`}
                                onClick={() => handleActivateUser(user.id)}
                                disabled={isViewOnly}
                                title={isViewOnly ? viewOnlyTip : t.activate}
                              >
                                ✅
                              </button>
                            )}
                            <button
                              className={`btn-icon ${isViewOnly ? 'disabled-view-only' : ''}`}
                              onClick={() => handleResetPassword(user.id)}
                              disabled={isViewOnly}
                              title={isViewOnly ? viewOnlyTip : t.resetPassword}
                            >
                              🔑
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersModal;
